import { MenuProps } from 'antd';
import { WorkspaceFramework } from '../../../types/Workspace';
import {
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOutlined,
  LeftOutlined,
  ProjectOutlined,
} from '@ant-design/icons';
import { BacklogCircle } from '../../../assets/CustomIcons/BacklogCircle';
import { ItemType } from 'antd/es/menu/interface';
import { SprintMenuIcon } from '../../../assets/CustomIcons/SprintIcon';
import { WorkspaceView, WorkspaceViewEntityType, WorkspaceViewGroup } from '../../../types/WorkspaceView';

export const TASK_ROW_MENU_KEYS = {
  moveToBacklog: 'move_to_backlog',
  moveToPlanning: 'move_to_planning',
  moveToSprint: 'addToSprint',
  addToPlanning: 'addToPlanning',
  removeFromSprint: 'remove_from_sprint',
  archive: 'archive',
  unarchive: 'unarchive',
  rename: 'rename',
  deleteForever: 'delete',
} as const;

export const taskRowMenuItems = (framework?: WorkspaceFramework, view?: WorkspaceView): ItemType[] => {
  const viewGroup = view?.group;
  const isTask = view?.entityType === WorkspaceViewEntityType.task;

  // Archive view: only Restore-to-Backlog + rename + delete forever.
  if (isTask && viewGroup === WorkspaceViewGroup.archive) {
    return [
      {
        key: TASK_ROW_MENU_KEYS.unarchive,
        label: 'Restore to Backlog',
        icon: <BacklogCircle width="1em" height="1em" />,
      },
      { key: TASK_ROW_MENU_KEYS.rename, label: 'Rename', icon: <EditOutlined /> },
      { type: 'divider' },
      {
        key: TASK_ROW_MENU_KEYS.deleteForever,
        label: <span>Delete Forever</span>,
        icon: <DeleteOutlined />,
        danger: true,
      },
    ];
  }

  // Triage view: move-to-* actions + archive + delete forever.
  if (isTask && viewGroup === WorkspaceViewGroup.triage) {
    const triageMoves: MenuProps['items'] = [
      {
        key: TASK_ROW_MENU_KEYS.moveToBacklog,
        label: 'Move to Backlog',
        icon: <BacklogCircle width="1em" height="1em" />,
      },
      ...(framework === WorkspaceFramework.scrum
        ? [
            { key: TASK_ROW_MENU_KEYS.addToPlanning, label: 'Move to Planning', icon: <ClockCircleOutlined /> },
            { key: TASK_ROW_MENU_KEYS.moveToSprint, label: 'Move to Sprint', icon: <SprintMenuIcon /> },
          ]
        : framework === WorkspaceFramework.kanban
          ? [{ key: TASK_ROW_MENU_KEYS.moveToSprint, label: 'Move to Kanban', icon: <ProjectOutlined /> }]
          : []),
    ];
    return [
      ...triageMoves,
      { key: TASK_ROW_MENU_KEYS.rename, label: 'Rename', icon: <EditOutlined /> },
      { type: 'divider' },
      { key: TASK_ROW_MENU_KEYS.archive, label: 'Move to Archive', icon: <FolderOutlined /> },
      {
        key: TASK_ROW_MENU_KEYS.deleteForever,
        label: <span>Delete Forever</span>,
        icon: <DeleteOutlined />,
        danger: true,
      },
    ];
  }

  const frameworkSpecificMenuItems: MenuProps['items'] =
    framework === WorkspaceFramework.scrum
      ? viewGroup === WorkspaceViewGroup.sprint
        ? [
            {
              key: TASK_ROW_MENU_KEYS.moveToPlanning,
              label: 'Move to Planning',
              icon: <LeftOutlined />,
            },
            {
              key: TASK_ROW_MENU_KEYS.removeFromSprint,
              label: 'Remove from Sprint',
              icon: <CloseOutlined />,
            },
          ]
        : [
            {
              key: TASK_ROW_MENU_KEYS.addToPlanning,
              label: <span>Add to Planning</span>,
              icon: <ClockCircleOutlined />,
            },
            {
              key: TASK_ROW_MENU_KEYS.moveToSprint,
              label: <span>Add to Sprint</span>,
              icon: <SprintMenuIcon />,
            },
          ]
      : framework === WorkspaceFramework.kanban
        ? viewGroup === WorkspaceViewGroup.kanban
          ? [
              {
                key: TASK_ROW_MENU_KEYS.removeFromSprint,
                label: 'Remove from Kanban',
                icon: <CloseOutlined />,
              },
            ]
          : [
              {
                key: TASK_ROW_MENU_KEYS.moveToSprint,
                label: <span>Add to Kanban</span>,
                icon: <ProjectOutlined />,
              },
            ]
        : [];
  const rowMenuItems: MenuProps['items'] = [
    ...(isTask ? frameworkSpecificMenuItems : []),
    {
      key: TASK_ROW_MENU_KEYS.rename,
      label: 'Rename',
      icon: <EditOutlined />,
    },
    { type: 'divider' },
    ...(isTask ? [{ key: TASK_ROW_MENU_KEYS.archive, label: <span>Archive</span>, icon: <FolderOutlined /> }] : []),
    {
      key: TASK_ROW_MENU_KEYS.deleteForever,
      label: <span>Delete</span>,
      icon: <DeleteOutlined />,
      danger: true,
    },
  ];

  return rowMenuItems;
};
